
import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { ServiceCategory, User, ServiceItem } from '../types';
import { Card } from './ui';
import { Tag, Search, Loader2, AlertCircle } from 'lucide-react';

export const PriceList = ({ user }: { user: User | null }) => {
   const [services, setServices] = useState<ServiceCategory[]>([]);
   const [searchTerm, setSearchTerm] = useState('');
   const [loading, setLoading] = useState(true);

   useEffect(() => {
      api.getServices(user?.id)
         .then(data => {
            setServices(data);
         })
         .catch(err => console.error("Failed to load services:", err))
         .finally(() => setLoading(false));
   }, [user?.id]);

   const filteredCategories = services.map((cat: ServiceCategory) => ({
      ...cat,
      services: cat.services.filter((s: ServiceItem) =>
         s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
   })).filter((cat: ServiceCategory) => cat.services.length > 0);

   return (
      <div className="space-y-6 animate-fade-in pb-20">
         <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
               <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Price List</h1>
               <p className="text-zinc-400">Current service catalog and base pricing.</p>
            </div>
            <div className="relative w-full md:w-64">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
               <input
                  type="text"
                  placeholder="Search services..."
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-zinc-700"
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
               />
            </div>
         </div>

         {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
               <Loader2 className="animate-spin text-blue-500 mb-4" size={32} />
               <p className="text-zinc-500">Loading catalog...</p>
            </div>
         ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {filteredCategories.map((cat: ServiceCategory) => (
                  <div key={cat.id} className="space-y-3">
                     <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                        <Tag size={14} /> {cat.name}
                     </h3>
                     <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden divide-y divide-zinc-800/50">
                        {cat.services.map((srv: ServiceItem) => (
                           <div key={srv.id} className="p-4 flex justify-between items-center hover:bg-zinc-800/30 transition-colors">
                              <span className="text-zinc-200 font-medium text-sm">{srv.name}</span>
                              <span className="font-mono font-bold text-white">{srv.price} EGP</span>
                           </div>
                        ))}
                     </div>
                  </div>
               ))}

               {services.length === 0 && (
                  <div className="col-span-full text-center py-12 border-2 border-dashed border-red-900/30 bg-red-900/10 rounded-xl">
                     <AlertCircle className="mx-auto text-red-400 mb-4" size={32} />
                     <h3 className="text-lg font-bold text-white">System Error: Catalog Empty</h3>
                     <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">This usually means the database permissions need to be reset. <br /><br />If you are the admin, please run the fix script (<code>db_v3_9_0_fix.txt</code>) in your Supabase SQL editor.</p>
                  </div>
               )}

               {services.length > 0 && filteredCategories.length === 0 && (
                  <div className="col-span-full text-center py-12 text-zinc-500">
                     No services found matching "{searchTerm}".
                  </div>
               )}
            </div>
         )}
      </div>
   );
};
